import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getCurrentMonth, getCurrentYear } from 'site-modules/shared/utils/time-util';
import { interpolateTemplate } from 'client/site-modules/lease-deals/utils/interpolate-template';
import { makeNiceName } from 'client/site-modules/shared/utils/nice-name';

export const FAQ_ANSWER_STRATEGY = {
  BASE: 'base',
  DEALS_COUNT: 'deals-count',
  DEALS_NEAR_BY: 'deals-near-by',
  MAKE_TIER: 'make-tier',
};

function buildDealVariables(deal) {
  if (!deal) return undefined;
  return {
    year: deal.year,
    make: deal.make,
    model: deal.model,
    payment: getPriceStringV2(deal.payment),
    term: deal.term,
    dueAtSigning: getPriceStringV2(deal.dueAtSigning),
  };
}

function buildBaseAnswer(entry, variables) {
  const template = entry.metadata('answer').value();
  if (!template) return undefined;
  return interpolateTemplate(template, variables);
}

function buildDealsCountAnswer(entry, variables, sortedDeals) {
  let template = entry.metadata('answer-two-deals').value();

  if (sortedDeals.length === 0) {
    template = entry.metadata('answer-no-deals').value();
  } else if (sortedDeals.length === 1) {
    template = entry.metadata('answer-one-deal').value();
  }

  if (!template) return undefined;

  return interpolateTemplate(template, variables);
}

const MAKE_TIERS = {
  top: [
    'toyota',
    'mini',
    'subaru',
    'jeep',
    'gmc',
    'honda',
    'mazda',
    'porsche',
    'acura',
    'lexus',
    'bmw',
    'mercedes-benz',
    'audi',
  ],
  mid: [
    'chevrolet',
    'volkswagen',
    'fiat',
    'buick',
    'dodge',
    'nissan',
    'hyundai',
    'kia',
    'infiniti',
    'land-rover',
    'jaguar',
  ],
  low: ['mitsubishi', 'alfa-romeo', 'ford', 'chrysler', 'cadillac', 'volvo', 'genesis'],
};

function getMakeTier(makeSlug) {
  if (MAKE_TIERS.top.includes(makeSlug)) return 'top';
  if (MAKE_TIERS.mid.includes(makeSlug)) return 'mid';
  if (MAKE_TIERS.low.includes(makeSlug)) return 'low';
  return 'none';
}

function buildDealsNearByAnswer(entry, variables, _sortedDeals, nearbyInventoryData) {
  const { inventoryCount = 0, lowestLease, highestLease } = nearbyInventoryData ?? {};

  const hasRange = lowestLease != null && highestLease != null;
  const templateKey = hasRange ? 'answer' : 'answer-no-range';
  const template = entry.metadata(templateKey).value();

  if (!template) return undefined;

  return interpolateTemplate(template, {
    ...variables,
    inventoryCount,
    lowestLease: getPriceStringV2(lowestLease),
    highestLease: getPriceStringV2(highestLease),
  });
}

function buildMakeTierAnswer(entry, variables) {
  const { make } = variables;
  const tier = getMakeTier(makeNiceName(make));
  const template = entry.metadata(`answer-${tier}`).value();

  if (!template) return undefined;

  return interpolateTemplate(template, variables);
}

const STRATEGY_BUILDERS = {
  [FAQ_ANSWER_STRATEGY.BASE]: buildBaseAnswer,
  [FAQ_ANSWER_STRATEGY.DEALS_COUNT]: buildDealsCountAnswer,
  [FAQ_ANSWER_STRATEGY.DEALS_NEAR_BY]: buildDealsNearByAnswer,
  [FAQ_ANSWER_STRATEGY.MAKE_TIER]: buildMakeTierAnswer,
};

export function getFaqDynamicAnswers({ leaseDealsData, faqEntry, nearbyInventoryData, make }) {
  if (!faqEntry) return {};

  const deals = leaseDealsData?.deals ?? [];
  const sortedDeals = [...deals].sort((a, b) => a.payment - b.payment);

  const variables = {
    month: getCurrentMonth(),
    year: getCurrentYear(),
    deal1: buildDealVariables(sortedDeals[0]),
    deal2: buildDealVariables(sortedDeals[1]),
    make,
  };

  return faqEntry.children().reduce((acc, entry) => {
    const strategy = entry.metadata('strategy').value();
    const builder = STRATEGY_BUILDERS[strategy];
    return {
      ...acc,
      [entry.id]: builder
        ? builder(entry, variables, sortedDeals, nearbyInventoryData)
        : STRATEGY_BUILDERS[FAQ_ANSWER_STRATEGY.BASE](entry, variables),
    };
  }, {});
}
